import { useState, useEffect, useRef } from 'react';
import { X, Search, Replace, CheckSquare, Square, AlertCircle } from 'lucide-react';
import useFlowStore from '../../stores/useFlowStore';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import toast from 'react-hot-toast';

const getNodeIcon = (type) => {
  const icons = {
    table: '🗄️',
    collection: '📄',
    fact: '📊',
    dimension: '📐',
    view: '👁️',
  };
  return icons[type] || '📋';
};

const FindReplaceModal = ({ isOpen, onClose }) => {
  const { nodes, updateNode } = useFlowStore();
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState(new Set());
  const findInputRef = useRef(null);
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();

  useEffect(() => {
    if (isOpen && findInputRef.current) {
      findInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!findText.trim()) {
      setResults([]);
      setSelectedResults(new Set());
      return;
    }

    const searchTerm = caseSensitive ? findText : findText.toLowerCase();
    const matches = [];

    nodes.forEach(node => {
      const fields = node.data?.fields || [];
      fields.forEach((field, fieldIndex) => {
        const fieldName = caseSensitive ? field.name : field.name.toLowerCase();
        
        const isMatch = exactMatch 
          ? fieldName === searchTerm 
          : fieldName.includes(searchTerm);

        if (isMatch) {
          matches.push({
            id: `${node.id}_${fieldIndex}`,
            nodeId: node.id,
            nodeName: node.data.name,
            nodeType: node.type,
            fieldIndex,
            fieldName: field.name,
            fieldType: field.type,
            fieldDescription: field.description,
          });
        }
      });
    });

    setResults(matches);
    // Auto-select all results
    setSelectedResults(new Set(matches.map(m => m.id)));
  }, [findText, caseSensitive, exactMatch, nodes]);

  const toggleSelection = (resultId) => {
    const newSelected = new Set(selectedResults);
    if (newSelected.has(resultId)) {
      newSelected.delete(resultId);
    } else {
      newSelected.add(resultId);
    }
    setSelectedResults(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedResults.size === results.length) {
      setSelectedResults(new Set());
    } else {
      setSelectedResults(new Set(results.map(r => r.id)));
    }
  };

  const handleReplace = async () => {
    if (!replaceText.trim()) {
      toast.error('Please enter a replacement text');
      return;
    }

    if (selectedResults.size === 0) {
      toast.error('Please select at least one field to replace');
      return;
    }

    const selectedCount = selectedResults.size;
    const affectedNodes = new Set(
      results
        .filter(r => selectedResults.has(r.id))
        .map(r => r.nodeName)
    ).size;

    await confirm({
      title: 'Replace Field Names',
      message: `Are you sure you want to replace "${findText}" with "${replaceText}" in ${selectedCount} field${selectedCount !== 1 ? 's' : ''} across ${affectedNodes} entit${affectedNodes !== 1 ? 'ies' : 'y'}? This action cannot be undone.`,
      confirmText: `Replace ${selectedCount} Field${selectedCount !== 1 ? 's' : ''}`,
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: () => {
        performReplace();
      }
    });
  };

  const performReplace = () => {
    let replacedCount = 0;
    const nodesToUpdate = new Map();

    // Group results by node
    results.forEach(result => {
      if (selectedResults.has(result.id)) {
        if (!nodesToUpdate.has(result.nodeId)) {
          const node = nodes.find(n => n.id === result.nodeId);
          nodesToUpdate.set(result.nodeId, {
            node,
            fieldsToUpdate: []
          });
        }
        nodesToUpdate.get(result.nodeId).fieldsToUpdate.push(result.fieldIndex);
      }
    });

    // Update each node
    nodesToUpdate.forEach(({ node, fieldsToUpdate }) => {
      const updatedFields = [...node.data.fields];
      fieldsToUpdate.forEach(fieldIndex => {
        updatedFields[fieldIndex] = {
          ...updatedFields[fieldIndex],
          name: replaceText
        };
        replacedCount++;
      });

      updateNode(node.id, { fields: updatedFields });
    });

    toast.success(`Successfully replaced ${replacedCount} field name${replacedCount !== 1 ? 's' : ''}!`);
    
    // Reset form
    setFindText('');
    setReplaceText('');
    setResults([]);
    setSelectedResults(new Set());
  };

  if (!isOpen) return null;

  const allSelected = results.length > 0 && selectedResults.size === results.length;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[70] p-4 pt-20 animate-fadeIn">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full animate-scaleIn">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Replace size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Find & Replace Fields
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search and replace field names across all entities
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>

          {/* Search Inputs */}
          <div className="p-6 space-y-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Find field name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  ref={findInputRef}
                  type="text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  placeholder="Enter field name to find..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Replace with
              </label>
              <div className="relative">
                <Replace className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Enter new field name..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={caseSensitive}
                  onChange={(e) => setCaseSensitive(e.target.checked)}
                  className="rounded"
                />
                <span className="text-gray-700 dark:text-gray-300">Case sensitive</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exactMatch}
                  onChange={(e) => setExactMatch(e.target.checked)}
                  className="rounded"
                />
                <span className="text-gray-700 dark:text-gray-300">Exact match</span>
              </label>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {findText && results.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <AlertCircle className="mx-auto mb-3 opacity-50" size={40} />
                <p>No fields found matching "{findText}"</p>
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="sticky top-0 bg-gray-50 dark:bg-gray-700/50 px-6 py-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    {allSelected ? (
                      <CheckSquare size={18} className="text-blue-600" />
                    ) : (
                      <Square size={18} />
                    )}
                    <span>
                      {selectedResults.size} of {results.length} selected
                    </span>
                  </button>
                </div>

                <div className="py-2">
                  {results.map((result) => {
                    const isSelected = selectedResults.has(result.id);
                    return (
                      <div
                        key={result.id}
                        onClick={() => toggleSelection(result.id)}
                        className={`px-6 py-3 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="pt-0.5">
                            {isSelected ? (
                              <CheckSquare size={18} className="text-blue-600" />
                            ) : (
                              <Square size={18} className="text-gray-400" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getNodeIcon(result.nodeType)}</span>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {result.nodeName}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                {result.nodeType}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                                {result.fieldName}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {result.fieldType}
                              </span>
                            </div>
                            {result.fieldDescription && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                                {result.fieldDescription}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Search className="mx-auto mb-3 opacity-50" size={40} />
                <p className="font-medium">Search for field names</p>
                <p className="text-sm mt-2">Enter a field name to find across all entities</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleReplace}
              disabled={selectedResults.size === 0 || !replaceText.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Replace {selectedResults.size > 0 && `(${selectedResults.size})`}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancel}
      />
    </>
  );
};

export default FindReplaceModal;
